import { useState } from 'preact/hooks';
import { GameStateHook, WORLD_UNLOCK_CONFIG } from '../utils/useGameState';
import { api } from '../utils/api';

const WORLDS = [
  { id: 1, name: 'Warehouse Basics', levelRange: [1, 20] as const },
  { id: 2, name: 'Shipping Yard', levelRange: [21, 50] as const },
  { id: 3, name: 'Factory Floor', levelRange: [51, 100] as const },
];

interface Props {
  selectedWorld: number;
  onSelectWorld: (worldId: number) => void;
  onPlayLevel: (levelId: number) => void;
  onBack: () => void;
  gameState: GameStateHook;
}

export function LevelSelect({ selectedWorld, onSelectWorld, onPlayLevel, onBack, gameState }: Props) {
  const [showUnlockDialog, setShowUnlockDialog] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  const world = WORLDS.find(w => w.id === selectedWorld) || WORLDS[0];
  const [start, end] = world.levelRange;
  const levels = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const handleWorldTab = (worldId: number) => {
    if (worldId === 1 || gameState.isWorldUnlocked(worldId)) {
      onSelectWorld(worldId);
    } else if (worldId === 3 && !gameState.isWorldUnlocked(2)) {
      // Must unlock World 2 before World 3
      const tg = (window as any).Telegram?.WebApp;
      tg?.showAlert?.('Unlock Shipping Yard (World 2) first!') ?? alert('Unlock Shipping Yard (World 2) first!');
    } else {
      setShowUnlockDialog(worldId);
    }
  };

  const handlePayStars = async (worldId: number) => {
    const itemId = `world_${worldId}`;
    setPurchasing(true);
    try {
      const result = await api.purchase(itemId);
      if (result?.invoiceUrl) {
        const tg = (window as any).Telegram?.WebApp;
        tg?.openInvoice?.(result.invoiceUrl, async (status: string) => {
          if (status === 'paid') {
            await gameState.refreshUser();
            setShowUnlockDialog(null);
            onSelectWorld(worldId);
          }
          setPurchasing(false);
        });
      } else {
        setPurchasing(false);
      }
    } catch {
      setPurchasing(false);
      const tg = (window as any).Telegram?.WebApp;
      tg?.showAlert?.('Purchase failed. Please try again.');
    }
  };

  const dialogWorld = showUnlockDialog ? WORLDS.find(w => w.id === showUnlockDialog) : null;
  const dialogConfig = showUnlockDialog ? WORLD_UNLOCK_CONFIG[showUnlockDialog] : null;

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={onBack}>Back</button>
        <h2 style={styles.title}>{world.name}</h2>
        <div style={{ width: 50 }} />
      </div>

      <div style={styles.worldTabs}>
        {WORLDS.map(w => {
          const locked = w.id > 1 && !gameState.isWorldUnlocked(w.id);
          return (
            <button
              key={w.id}
              style={{
                ...styles.worldTab,
                ...(w.id === selectedWorld ? styles.worldTabActive : {}),
                ...(locked ? styles.worldTabLocked : {}),
              }}
              onClick={() => handleWorldTab(w.id)}
            >
              {locked ? `${w.id}` : w.id}
            </button>
          );
        })}
      </div>

      <div style={styles.grid}>
        {levels.map(id => {
          const prog = gameState.progress.get(id);
          const completed = prog?.completed;
          const stars = prog?.stars ?? 0;
          // Level is locked if previous level isn't completed (except level 1)
          const prevCompleted = id === 1 || gameState.progress.get(id - 1)?.completed;
          const locked = !prevCompleted && !completed;

          return (
            <button
              key={id}
              style={{
                ...styles.levelBtn,
                ...(completed ? styles.levelCompleted : {}),
                ...(locked ? styles.levelLocked : {}),
              }}
              onClick={() => !locked && onPlayLevel(id)}
              disabled={locked}
            >
              <span style={styles.levelNum}>{id}</span>
              {completed && (
                <div style={styles.starsRow}>
                  {[1, 2, 3].map(s => (
                    <span key={s} style={{ ...styles.star, opacity: s <= stars ? 1 : 0.2 }}>
                      {s <= stars ? '★' : '☆'}
                    </span>
                  ))}
                </div>
              )}
              {locked && <span style={styles.lock}>locked</span>}
            </button>
          );
        })}
      </div>

      {/* Unlock dialog overlay */}
      {showUnlockDialog && dialogWorld && dialogConfig && (
        <div style={styles.overlay}>
          <div style={styles.dialogCard}>
            <h3 style={styles.dialogTitle}>Unlock {dialogWorld.name}</h3>

            <button
              style={{ ...styles.dialogBtn, ...styles.dialogBtnPrimary }}
              onClick={() => handlePayStars(showUnlockDialog)}
              disabled={purchasing}
            >
              {purchasing ? 'Processing...' : `Pay ${dialogConfig.starsPrice} Stars`}
            </button>

            <button
              style={styles.dialogCancel}
              onClick={() => setShowUnlockDialog(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#4ecdc4',
    fontSize: '16px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '8px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#eee',
  },
  worldTabs: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    padding: '0 16px 12px',
  },
  worldTab: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.05)',
    color: '#aaa',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  worldTabActive: {
    background: '#4ecdc4',
    color: '#1a1a2e',
    borderColor: '#4ecdc4',
  },
  worldTabLocked: {
    opacity: 0.5,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  grid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '8px',
    padding: '16px',
    overflowY: 'auto',
    alignContent: 'start',
  },
  levelBtn: {
    aspectRatio: '1',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.06)',
    color: '#eee',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontFamily: 'inherit',
    gap: '2px',
    position: 'relative',
  },
  levelCompleted: {
    background: 'rgba(78, 205, 196, 0.1)',
    borderColor: 'rgba(78, 205, 196, 0.3)',
  },
  levelLocked: {
    opacity: 0.4,
    cursor: 'default',
  },
  levelNum: {
    fontSize: '16px',
    fontWeight: '700',
  },
  starsRow: {
    display: 'flex',
    gap: '1px',
  },
  star: {
    fontSize: '10px',
    color: '#feca57',
  },
  lock: {
    fontSize: '8px',
    color: '#666',
    position: 'absolute',
    bottom: '4px',
  },
  // Unlock dialog
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  dialogCard: {
    background: '#2a2a4a',
    borderRadius: '20px',
    padding: '28px 24px',
    textAlign: 'center',
    maxWidth: '300px',
    width: '90%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  dialogTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#eee',
  },
  dialogBtn: {
    border: 'none',
    borderRadius: '12px',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: '100%',
  },
  dialogBtnPrimary: {
    background: 'linear-gradient(135deg, #4ecdc4, #44b3aa)',
    color: '#1a1a2e',
  },
  dialogCancel: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '8px',
  },
};
